import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Wand2, Sparkles, Zap, MonitorPlay, ArrowRight } from 'lucide-react';
import Hero from '../components/Hero';
import AIPlanner from '../components/AIPlanner';
import { FadeIn, SectionLabel } from '../components/Shared';
import Pricing from './Pricing';
import Portfolio from './Portfolio';
import Security from './Security';
import About from './About';
import FAQ from './FAQ';
import Process from './Process';

/**
 * Premium one-page scrolling layout.
 * Order: Hero (with orbital service cards) → Pricing → AI Website Demo (featured) →
 *        AI Planner → Portfolio → Security → About → Process → FAQ.
 * Contact has its own standalone /contact route — no inline section on homepage anymore.
 * Services has no dedicated section: the orbital cards around the globe in the hero
 * cover all six services with click-to-expand detail panels.
 */
const OnePage = () => {
    const location = useLocation();

    // Smooth-scroll to the hash target (e.g. /#pricing) when arriving on the page
    useEffect(() => {
        if (location.hash) {
            const id = location.hash.slice(1);
            requestAnimationFrame(() => {
                const el = document.getElementById(id);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
        } else {
            window.scrollTo({ top: 0, behavior: 'instant' });
        }
    }, [location.pathname, location.hash]);

    // Reveal sections on scroll — pairs with the .is-in-view CSS in index.css.
    useEffect(() => {
        const sections = document.querySelectorAll(
            '[data-testid="one-page-root"] > section[id]:not(#home)'
        );
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('is-in-view');
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.08, rootMargin: '0px 0px -80px 0px' }
        );
        sections.forEach((s) => observer.observe(s));
        return () => observer.disconnect();
    }, []);

    const scrollToId = (id) => (e) => {
        e.preventDefault();
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <main data-testid="one-page-root">
            {/* 1. Epic hero — also houses the 6 orbital service cards around the globe */}
            <section id="home" data-testid="section-home">
                <Hero />
            </section>

            {/* 2. Pricing */}
            <section id="pricing" data-testid="section-pricing" className="scroll-mt-20">
                <Pricing />
            </section>

            {/* 4. AI Website Demo — premium featured section (replaces the old small CTA) */}
            <section
                id="website-demo"
                data-testid="section-website-demo"
                className="scroll-mt-20 relative py-24 sm:py-28 overflow-hidden"
            >
                {/* Cinematic ambient layer */}
                <div
                    className="absolute inset-0 pointer-events-none -z-10"
                    aria-hidden="true"
                    style={{
                        background:
                            'radial-gradient(55% 45% at 50% 0%, rgba(56,189,248,0.22) 0%, transparent 70%),' +
                            'radial-gradient(40% 35% at 12% 90%, rgba(99,102,241,0.16) 0%, transparent 70%),' +
                            'radial-gradient(35% 30% at 88% 95%, rgba(125,211,252,0.16) 0%, transparent 70%)',
                    }}
                />

                <div className="max-w-6xl mx-auto px-6 lg:px-8 relative">
                    <FadeIn>
                        <div className="text-center max-w-3xl mx-auto mb-10">
                            <SectionLabel>AI Website Demo</SectionLabel>
                            <h2
                                className="mt-5 font-display text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tight text-[#0F172A] leading-[1.08]"
                                data-testid="website-demo-headline"
                            >
                                See your website <br className="hidden sm:block" />
                                <span className="text-[#2563EB]">before we build it.</span>
                            </h2>
                            <p className="mt-5 text-[17px] sm:text-[18px] text-[#475569] leading-relaxed">
                                Upload inspiration, describe your business, and watch AI generate a
                                live interactive website demo in seconds.
                            </p>
                        </div>
                    </FadeIn>

                    {/* Premium glass feature card */}
                    <FadeIn delay={0.1}>
                        <div
                            className="relative rounded-[28px] sm:rounded-[32px] overflow-hidden"
                            style={{
                                background:
                                    'linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(239,246,255,0.85) 100%)',
                                backdropFilter: 'blur(24px) saturate(140%)',
                                WebkitBackdropFilter: 'blur(24px) saturate(140%)',
                                border: '1.5px solid rgba(255,255,255,0.6)',
                                boxShadow:
                                    '0 20px 60px rgba(37,99,235,0.18), 0 8px 24px rgba(37,99,235,0.10), 0 0 0 1px rgba(255,255,255,0.4) inset',
                            }}
                            data-testid="website-demo-feature-card"
                        >
                            {/* Decorative blue glow */}
                            <div
                                className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full pointer-events-none"
                                aria-hidden="true"
                                style={{
                                    background:
                                        'radial-gradient(circle, rgba(59,130,246,0.35) 0%, transparent 60%)',
                                    filter: 'blur(20px)',
                                }}
                            />
                            <div
                                className="absolute -bottom-24 -left-24 w-[340px] h-[340px] rounded-full pointer-events-none"
                                aria-hidden="true"
                                style={{
                                    background:
                                        'radial-gradient(circle, rgba(125,211,252,0.30) 0%, transparent 60%)',
                                    filter: 'blur(20px)',
                                }}
                            />

                            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 p-8 sm:p-12 lg:p-14">
                                {/* Left: copy + CTAs */}
                                <div className="lg:col-span-7 flex flex-col justify-center">
                                    <div className="inline-flex items-center gap-2 self-start px-3.5 py-1.5 rounded-full bg-white/90 border border-[#DBE6FF] shadow-[0_4px_16px_rgba(37,99,235,0.10)]">
                                        <Sparkles className="w-3.5 h-3.5 text-[#2563EB]" />
                                        <span className="font-mono text-[10.5px] tracking-[0.18em] text-[#0F172A] uppercase font-semibold">
                                            Live · Powered by AI
                                        </span>
                                    </div>

                                    <h3 className="mt-5 font-display text-2xl sm:text-3xl md:text-[34px] font-semibold tracking-tight text-[#0F172A] leading-[1.18]">
                                        Watch your website appear in 60 seconds.
                                    </h3>

                                    <p className="mt-4 text-[15.5px] sm:text-[16px] text-[#475569] leading-relaxed max-w-xl">
                                        Upload ideas, images and preferences and instantly preview an
                                        AI-generated website demo — live, interactive, and built around
                                        your brand.
                                    </p>

                                    {/* Feature bullets */}
                                    <ul className="mt-5 space-y-2.5">
                                        {[
                                            { icon: Wand2,       text: 'Quick questionnaire — about your business, style and goals' },
                                            { icon: Zap,         text: 'Live HTML & CSS preview generated in under a minute' },
                                            { icon: MonitorPlay, text: 'Approve, regenerate, or publish your live site instantly' },
                                        ].map(({ icon: Icon, text }) => (
                                            <li key={text} className="flex items-start gap-3 text-[14px] sm:text-[15px] text-[#334155]">
                                                <span className="inline-flex w-7 h-7 rounded-lg items-center justify-center bg-[#EEF4FF] text-[#2563EB] shrink-0">
                                                    <Icon className="w-3.5 h-3.5" />
                                                </span>
                                                <span className="leading-relaxed">{text}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    {/* CTAs */}
                                    <div className="mt-7 flex flex-col sm:flex-row gap-3">
                                        <Link
                                            to="/website-demo"
                                            className="inline-flex items-center justify-center gap-2 bg-gradient-to-br from-[#3B82F6] to-[#1D4ED8] text-white font-semibold text-[15px] px-7 py-3.5 rounded-full transition-all hover:opacity-95"
                                            style={{
                                                boxShadow:
                                                    '0 6px 22px rgba(37,99,235,0.35), 0 12px 40px rgba(37,99,235,0.18)',
                                            }}
                                            data-testid="cta-start-website-demo"
                                        >
                                            <Wand2 className="w-4 h-4" />
                                            Start AI Website Demo
                                            <ArrowRight className="w-4 h-4" />
                                        </Link>
                                        <a
                                            href="#planner"
                                            onClick={scrollToId('planner')}
                                            className="inline-flex items-center justify-center gap-2 bg-white/95 hover:bg-white text-[#0F172A] font-semibold text-[15px] px-7 py-3.5 rounded-full border border-[#DBE6FF] transition-all"
                                            style={{ boxShadow: '0 4px 15px rgba(59,130,246,0.10)' }}
                                            data-testid="cta-view-preview-flow"
                                        >
                                            <MonitorPlay className="w-4 h-4 text-[#2563EB]" />
                                            View Live Preview Flow
                                        </a>
                                    </div>

                                    {/* Trust micro-row */}
                                    <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[12.5px] text-[#64748B]">
                                        <div className="flex items-center gap-1.5">
                                            <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E]" />
                                            No signup required
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6]" />
                                            Free preview
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <span className="w-1.5 h-1.5 rounded-full bg-[#A855F7]" />
                                            Powered by Gemini
                                        </div>
                                    </div>
                                </div>

                                {/* Right: mock browser preview */}
                                <div className="lg:col-span-5 flex items-center">
                                    <div
                                        className="w-full rounded-2xl overflow-hidden bg-white border border-[#DBE6FF]"
                                        style={{ boxShadow: '0 20px 50px rgba(15,23,42,0.14), 0 8px 20px rgba(37,99,235,0.10)' }}
                                        aria-hidden="true"
                                    >
                                        {/* Mock browser chrome */}
                                        <div className="flex items-center gap-1.5 px-3.5 py-2.5 bg-[#F8FAFC] border-b border-[#E2E8F0]">
                                            <span className="w-2.5 h-2.5 rounded-full bg-[#FCA5A5]" />
                                            <span className="w-2.5 h-2.5 rounded-full bg-[#FCD34D]" />
                                            <span className="w-2.5 h-2.5 rounded-full bg-[#86EFAC]" />
                                            <div className="ml-2 flex-1 h-5 rounded-md bg-white border border-[#E2E8F0] flex items-center px-2">
                                                <span className="font-mono text-[10px] text-[#94A3B8] truncate">
                                                    yourbrand.netlify.app
                                                </span>
                                            </div>
                                        </div>
                                        {/* Faux site preview */}
                                        <div className="p-5 bg-gradient-to-br from-[#EFF6FF] to-white">
                                            <div className="h-3 w-16 rounded-full bg-[#2563EB]/15 mb-4" />
                                            <div className="h-4 w-36 rounded-md bg-[#0F172A]/85 mb-2" />
                                            <div className="h-3 w-44 rounded-md bg-[#0F172A]/35 mb-5" />
                                            <div className="grid grid-cols-2 gap-2.5 mb-4">
                                                <div className="h-16 rounded-lg bg-white border border-[#DBE6FF]" />
                                                <div className="h-16 rounded-lg bg-white border border-[#DBE6FF]" />
                                                <div className="h-16 rounded-lg bg-white border border-[#DBE6FF]" />
                                                <div className="h-16 rounded-lg bg-white border border-[#DBE6FF]" />
                                            </div>
                                            <div className="flex gap-2">
                                                <div className="h-8 flex-1 rounded-full bg-gradient-to-br from-[#3B82F6] to-[#1D4ED8]" />
                                                <div className="h-8 w-20 rounded-full bg-white border border-[#DBE6FF]" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </FadeIn>
                </div>
            </section>

            {/* 5. AI Planner — now directly under the website demo, still inside the Pricing region */}
            <section
                id="planner"
                data-testid="section-planner"
                className="scroll-mt-20 relative py-24 overflow-hidden"
            >
                {/* Cinematic ambient layer — mirrors the hero atmosphere */}
                <div
                    className="absolute inset-0 pointer-events-none -z-10"
                    aria-hidden="true"
                    style={{
                        background:
                            'radial-gradient(50% 40% at 50% 0%, rgba(56,189,248,0.18) 0%, transparent 70%),' +
                            'radial-gradient(40% 35% at 18% 90%, rgba(99,102,241,0.14) 0%, transparent 70%),' +
                            'radial-gradient(35% 30% at 85% 95%, rgba(125,211,252,0.14) 0%, transparent 70%)',
                    }}
                />
                <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
                    <FadeIn>
                        <div className="text-center max-w-2xl mx-auto mb-10">
                            <SectionLabel>AI Planner</SectionLabel>
                            <h2 className="mt-5 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-[#1A1A2E] leading-[1.15]">
                                Plan your website with AI.
                            </h2>
                            <p className="mt-4 text-[17px] text-[#4A4A5A] leading-relaxed">
                                Tell us about your business and our AI creates your complete
                                website plan.
                            </p>
                        </div>
                    </FadeIn>
                    <FadeIn delay={0.1}>
                        <div className="mx-auto max-w-[640px]">
                            <AIPlanner testId="planner-onepage" height="h-[600px]" />
                        </div>
                    </FadeIn>
                </div>
            </section>

            {/* 6. Portfolio */}
            <section id="portfolio" data-testid="section-portfolio" className="scroll-mt-20">
                <Portfolio />
            </section>

            {/* 7. Security */}
            <section id="security" data-testid="section-security" className="scroll-mt-20">
                <Security />
            </section>

            {/* 8. About */}
            <section id="about" data-testid="section-about" className="scroll-mt-20">
                <About />
            </section>

            {/* 9. Process — How we work */}
            <section id="process" data-testid="section-process" className="scroll-mt-20">
                <Process />
            </section>

            {/* 10. FAQ */}
            <section id="faq" data-testid="section-faq" className="scroll-mt-20">
                <FAQ />
            </section>
        </main>
    );
};

export default OnePage;
